import { motion, type HTMLMotionProps, type Easing } from 'motion/react'

const EASE_OUT: Easing = 'easeOut'
const EASE_IN_OUT: Easing = 'easeInOut'
const EASE_CUBIC: Easing = [0.22, 1, 0.36, 1]

type Animation = 'idle' | 'breathing' | 'loading' | 'none'

interface PelviaLogoProps extends Omit<HTMLMotionProps<'span'>, 'children'> {
  size?: number | string
  animation?: Animation
  withText?: boolean
  alt?: string
}

const src = '/static/app/pelvia-logo.svg'
const srcWithText = '/static/app/pelvia-logo-w-text.svg'

export default function PelviaLogo({
  size = 32,
  animation = 'idle',
  withText = false,
  alt = 'Pelvia',
  className,
  style,
  ...rest
}: PelviaLogoProps) {
  const dim = typeof size === 'number' ? `${size}px` : size

  const variants = (() => {
    switch (animation) {
      case 'breathing':
        return {
          initial: { opacity: 0, scale: 0.6, rotate: -12 },
          animate: {
            opacity: 1,
            scale: [1, 1.05, 1],
            rotate: 0,
            transition: {
              opacity: { duration: 0.5, ease: EASE_OUT },
              rotate: { duration: 0.7, ease: EASE_CUBIC },
              scale: {
                duration: 4.5,
                ease: EASE_IN_OUT,
                repeat: Infinity,
                repeatType: 'mirror' as const,
                delay: 0.5,
              },
            },
          },
          whileHover: { scale: 1.12, rotate: 6, transition: { duration: 0.3 } },
        }
      case 'loading':
        // Calm "breathing" pulse + gentle float — no spin (a rotating wordmark
        // reads as broken). The radiating glow lives in CSS on the loader host.
        return {
          initial: { opacity: 0, scale: 0.82, y: 4 },
          animate: {
            opacity: [0.7, 1, 0.7],
            scale: [0.97, 1.03, 0.97],
            y: [3, -3, 3],
            transition: {
              opacity: { duration: 2.4, ease: EASE_IN_OUT, repeat: Infinity },
              scale: { duration: 2.4, ease: EASE_IN_OUT, repeat: Infinity },
              y: { duration: 3.2, ease: EASE_IN_OUT, repeat: Infinity },
            },
          },
        }
      case 'none':
        return {
          initial: false as const,
          animate: { opacity: 1 },
        }
      case 'idle':
      default:
        return {
          initial: { opacity: 0, scale: 0.75, rotate: -8 },
          animate: {
            opacity: 1,
            scale: 1,
            rotate: 0,
            transition: {
              type: 'spring' as const,
              stiffness: 180,
              damping: 14,
              mass: 0.8,
            },
          },
          whileHover: {
            rotate: [0, -8, 8, -4, 0],
            transition: { duration: 0.7, ease: EASE_IN_OUT },
          },
          whileTap: { scale: 0.92 },
        }
    }
  })()

  return (
    <motion.span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: withText ? 'auto' : dim,
        height: dim,
        lineHeight: 0,
        transformOrigin: 'center',
        willChange: 'transform, opacity',
        ...style,
      }}
      {...variants}
      {...rest}
    >
      <img
        src={withText ? srcWithText : src}
        alt={alt}
        draggable={false}
        // Size off the span so `size` controls both modes without distortion.
        // With text the span width is `auto`, so size by height and let width
        // follow. Without text the span is a square box, so fill it with
        // object-fit: contain — preserves the mark's aspect ratio (no squeeze).
        style={
          withText
            ? { display: 'block', height: '100%', width: 'auto', userSelect: 'none' }
            : { display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' }
        }
      />
    </motion.span>
  )
}
